import { useEffect, useRef } from 'react';
import { X, ExternalLink, Calendar, Tag, AlignLeft, MessageSquare, Zap } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import type { Ticket } from '../types';
import { Avatar } from './Avatar';
import { Markdown } from './Markdown';
import { normalizeComments } from '../lib/comments';
import { ActionBar } from './actions/ActionModal';
import { ThumbsWidget } from './feedback/ThumbsWidget';
import { track } from '../lib/telemetry';

const PRIORITY_LABELS: Record<number, string> = { 0: 'No Priority', 1: 'Urgent', 2: 'High', 3: 'Medium', 4: 'Low' };
const PRIORITY_CLASSES: Record<number, string> = {
  1: 'bg-red-50 text-red-600 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-900/30',
  2: 'bg-orange-50 text-orange-600 border-orange-200 dark:bg-orange-900/20 dark:text-orange-400 dark:border-orange-900/30',
  3: 'bg-yellow-50 text-yellow-600 border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-400 dark:border-yellow-900/30',
  4: 'bg-gray-100 text-gray-600 border-gray-200 dark:bg-white/5 dark:text-gray-400 dark:border-white/10',
};

interface TicketDetailPanelProps {
  ticket: Ticket | null;
  onClose: () => void;
  role?: string;
}

export function TicketDetailPanel({ ticket, onClose, role }: TicketDetailPanelProps) {
  const mountTsRef = useRef<number | null>(null);
  const actionTakenRef = useRef(false);

  useEffect(() => {
    if (!ticket) return;
    mountTsRef.current = Date.now();
    actionTakenRef.current = false;
    track('ticket.detail.opened', 'feature_use', {
      ticketId: ticket.ticketId,
      projectId: ticket.projectId ?? '',
      ticketStatus: ticket.status,
      ticketType: ticket.type,
    });
    return () => {
      if (!actionTakenRef.current && mountTsRef.current !== null) {
        track('ticket.detail.abandoned', 'abandonment', {
          ticketId: ticket.ticketId,
          projectId: ticket.projectId ?? '',
          durationMs: Date.now() - mountTsRef.current,
        });
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket?.ticketId]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <AnimatePresence>
      {ticket && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-black/20 dark:bg-black/40 backdrop-blur-sm"
            onClick={onClose}
          />
          {/* Panel */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed right-0 top-0 bottom-0 z-50 w-full max-w-lg bg-white dark:bg-[hsl(220,15%,12%)] shadow-2xl border-l border-gray-200 dark:border-white/10 flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-start justify-between gap-3 p-5 border-b border-gray-100 dark:border-white/5 flex-shrink-0">
              <div className="flex-1 min-w-0">
                <h2 className="text-title text-gray-900 dark:text-white">{ticket.title}</h2>
                {/* Badges row */}
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  <span className="text-meta px-2 py-0.5 rounded-full border border-gray-200 dark:border-white/10 bg-gray-100 dark:bg-white/5 text-gray-600 dark:text-gray-400 font-medium capitalize">
                    {ticket.status?.replace('_', ' ')}
                  </span>
                  <span className="text-meta px-2 py-0.5 rounded-full border border-gray-200 dark:border-white/10 bg-gray-100 dark:bg-white/5 text-gray-600 dark:text-gray-400 font-medium capitalize">
                    {ticket.type?.replace('_', ' ')}
                  </span>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-white/5 transition-colors flex-shrink-0"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Scrollable body */}
            <div className="flex-1 overflow-y-auto p-5 space-y-5">

              {/* Metadata grid */}
              <div className="grid grid-cols-2 gap-3">
                {ticket.assigneeName && (
                  <div>
                    <p className="text-meta text-gray-400 dark:text-gray-500 mb-1">Assignee</p>
                    <div className="flex items-center gap-1.5">
                      <Avatar user={{ fullName: ticket.assigneeName, profilePictureUrl: null }} size="sm" />
                      <span className="text-meta text-gray-700 dark:text-gray-300">{ticket.assigneeName}</span>
                    </div>
                  </div>
                )}
                {ticket.priority != null && ticket.priority > 0 && (
                  <div>
                    <p className="text-meta text-gray-400 dark:text-gray-500 mb-1">Priority</p>
                    <span className={`text-meta px-2 py-0.5 rounded-full border ${PRIORITY_CLASSES[ticket.priority]}`}>
                      {ticket.priorityLabel ?? PRIORITY_LABELS[ticket.priority]}
                    </span>
                  </div>
                )}
                {ticket.dueDate && (
                  <div>
                    <p className="text-meta text-gray-400 dark:text-gray-500 mb-1">Due Date</p>
                    <span className="text-meta text-gray-700 dark:text-gray-300 flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      {ticket.dueDate}
                    </span>
                  </div>
                )}
                {ticket.estimate != null && (
                  <div>
                    <p className="text-meta text-gray-400 dark:text-gray-500 mb-1">Estimate</p>
                    <span className="text-meta text-gray-700 dark:text-gray-300">{ticket.estimate} pts</span>
                  </div>
                )}
              </div>

              {/* Labels */}
              {(ticket.labels ?? []).length > 0 && (
                <div>
                  <p className="text-meta text-gray-400 dark:text-gray-500 mb-2 flex items-center gap-1"><Tag className="h-4 w-4" /> Labels</p>
                  <div className="flex flex-wrap gap-1.5">
                    {(ticket.labels ?? []).map(label => (
                      <span key={label} className="text-meta px-2 py-0.5 rounded-full bg-gray-100 dark:bg-white/5 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-white/10">{label}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Description */}
              {(ticket.description ?? ticket.aiSummary) && (
                <div>
                  <p className="text-meta text-gray-400 dark:text-gray-500 mb-2 flex items-center gap-1"><AlignLeft className="h-4 w-4" /> Description</p>
                  <Markdown className={ticket.aiSummary && !ticket.description ? 'text-gray-500 dark:text-gray-400 italic' : 'text-gray-700 dark:text-gray-300'}>
                    {ticket.description ?? ticket.aiSummary ?? ''}
                  </Markdown>
                  {ticket.aiSummary && (
                    <ThumbsWidget
                      contentType="ticket_summary"
                      contentId={`ticket-summary-${ticket.ticketId}`}
                      compact
                    />
                  )}
                </div>
              )}

              {/* Links / Artifacts */}
              {(ticket.artifacts ?? []).length > 0 && (
                <div>
                  <p className="text-meta text-gray-400 dark:text-gray-500 mb-2 flex items-center gap-1"><ExternalLink className="h-4 w-4" /> Links</p>
                  <div className="space-y-1.5">
                    {(ticket.artifacts ?? []).map((a, i) => (
                      <a
                        key={i}
                        href={a.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => {
                          actionTakenRef.current = true;
                          track('ticket.link_clicked', 'action', { ticketId: ticket.ticketId, artifactType: a.type });
                        }}
                        className="flex items-center gap-2 text-meta text-primary hover:underline"
                      >
                        <ExternalLink className="h-4 w-4 flex-shrink-0" />
                        {a.label ?? a.type}
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Recent Comments — structured entries (author, time, body).
                  normalizeComments also absorbs pre-2026-08-15 documents
                  that still store the legacy joined-string form. */}
              {normalizeComments(ticket.recentComments).length > 0 && (
                <div>
                  <p className="text-meta text-gray-400 dark:text-gray-500 mb-2 flex items-center gap-1"><MessageSquare className="h-4 w-4" /> Recent Comments</p>
                  <div className="space-y-4">
                    {normalizeComments(ticket.recentComments).map((c, i) => (
                      <div key={i}>
                        {(c.author || c.createdAt) && (
                          <p className="flex items-baseline gap-2 mb-0.5">
                            {c.author && (
                              <span className="text-meta font-medium text-gray-700 dark:text-gray-300">{c.author}</span>
                            )}
                            {c.createdAt && (
                              <span className="text-meta text-gray-400 dark:text-gray-500">
                                {new Date(c.createdAt).toLocaleDateString()}
                              </span>
                            )}
                          </p>
                        )}
                        <Markdown className="text-gray-500 dark:text-gray-400">{c.text}</Markdown>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Suggested Actions */}
              {(ticket.suggestedActions?.length ?? 0) > 0 && (
                <div className="pt-4 border-t border-gray-100 dark:border-white/5">
                  <p className="text-meta text-gray-400 dark:text-gray-500 mb-2.5 flex items-center gap-1.5 font-medium uppercase tracking-wide">
                    <Zap className="h-4 w-4 text-primary" />
                    Suggested Actions
                  </p>
                  <ActionBar
                    actions={ticket.suggestedActions ?? []}
                    role={role}
                    ticketId={ticket.ticketId}
                    projectId={ticket.projectId}
                    completions={ticket.completions}
                    max={4}
                    onBeforeOpen={(a) => {
                      actionTakenRef.current = true;
                      track('ticket.action_clicked', 'action', { ticketId: ticket.ticketId, actionId: a.actionId });
                    }}
                  />
                </div>
              )}

              {/* Source timestamps */}
              {(ticket.sourceCreatedAt ?? ticket.createdAt) && (
                <div className="pt-3 border-t border-gray-100 dark:border-white/5">
                  <p className="text-meta text-gray-400 dark:text-gray-500">
                    Created {new Date(ticket.sourceCreatedAt ?? ticket.createdAt).toLocaleDateString()}
                    {ticket.sourceUpdatedAt && ` · Updated ${new Date(ticket.sourceUpdatedAt).toLocaleDateString()}`}
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
